
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Check, 
  Clock, 
  Download, 
  Eye, 
  FileCheck, 
  FilePenLine, 
  FileText, 
  FileX, 
  MessageSquare, 
  MoreHorizontal, 
  Search, 
  X,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { DocumentSubmission, useDocumentSubmissions } from '@/hooks/useDocumentSubmissions';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { ResponsiveTable } from '@/components/ui/responsive-table';

interface DocumentsTableProps {
  onViewDocument: (document: DocumentSubmission) => void;
  onReviewDocument: (document: DocumentSubmission) => void;
}

export function DocumentsTable({ onViewDocument, onReviewDocument }: DocumentsTableProps) {
  const [search, setSearch] = useState('');
  const { fetchDocuments, getDocumentUrl } = useDocumentSubmissions();
  const isMobile = useIsMobile();
  
  const { data: documents, isLoading, refetch } = useQuery({
    queryKey: ['documents'],
    queryFn: fetchDocuments
  });

  const filteredDocuments = documents?.filter(doc => 
    doc.title.toLowerCase().includes(search.toLowerCase()) ||
    doc.document_type.toLowerCase().includes(search.toLowerCase()) ||
    doc.state?.toLowerCase().includes(search.toLowerCase()) ||
    doc.file_name.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const handleDownload = async (document: DocumentSubmission) => {
    try {
      const url = await getDocumentUrl(document.file_path);
      
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.file_name;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs"><Clock className="h-3 w-3 mr-1" /> {!isMobile && 'Pending'}</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs"><Check className="h-3 w-3 mr-1" /> {!isMobile && 'Approved'}</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs"><X className="h-3 w-3 mr-1" /> {!isMobile && 'Rejected'}</Badge>;
      case 'review':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs"><FilePenLine className="h-3 w-3 mr-1" /> {!isMobile && 'In Review'}</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  const renderDocumentTypeIcon = (type: string) => {
    switch (type) {
      case 'certificate':
        return <FileCheck className="h-4 w-4 text-green-600" />;
      case 'report':
        return <FileText className="h-4 w-4 text-blue-600" />;
      case 'regulatory':
        return <FileText className="h-4 w-4 text-red-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-[250px]" />
          <Skeleton className="h-10 w-[100px]" />
        </div>
        <Card>
          <div className="p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="py-4">
                <div className="flex justify-between items-center">
                  <Skeleton className="h-5 w-[400px]" />
                  <Skeleton className="h-8 w-[120px]" />
                </div>
                <div className="mt-2">
                  <Skeleton className="h-4 w-[300px]" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => refetch()}
          title="Refresh documents"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {filteredDocuments.length === 0 ? (
        <div className="bg-muted/40 rounded-lg p-8 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No documents found</h3>
          <p className="text-muted-foreground mb-4">
            {search ? 'Try a different search term or' : 'Get started by'} submitting a new document.
          </p>
        </div>
      ) : (
        <ResponsiveTable>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="p-2 md:p-4">Title</TableHead>
                  {!isMobile && <TableHead>Type</TableHead>}
                  {!isMobile && <TableHead>State</TableHead>}
                  {!isMobile && <TableHead>Submitted</TableHead>}
                  <TableHead className="p-2 md:p-4">Status</TableHead>
                  <TableHead className="text-right p-2 md:p-4"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell className="font-medium p-2 md:p-4">
                      <div className="flex items-center gap-2">
                        {renderDocumentTypeIcon(document.document_type)}
                        <span className="truncate max-w-[120px] md:max-w-[250px]">{document.title}</span>
                      </div>
                      {isMobile ? (
                        <div className="flex flex-col mt-1">
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(document.submitted_at), 'MMM d')}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {document.document_type.charAt(0).toUpperCase() + document.document_type.slice(1)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground block mt-1 truncate max-w-[250px]">
                          {document.file_name}
                        </span>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        {document.document_type.charAt(0).toUpperCase() + document.document_type.slice(1)}
                      </TableCell>
                    )}
                    {!isMobile && <TableCell>{document.state || '-'}</TableCell>}
                    {!isMobile && (
                      <TableCell>
                        {format(new Date(document.submitted_at), 'MMM d, yyyy')}
                      </TableCell>
                    )}
                    <TableCell className="p-2 md:p-4">{renderStatusBadge(document.status)}</TableCell>
                    <TableCell className="text-right p-2 md:p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onViewDocument(document)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownload(document)}>
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </DropdownMenuItem>
                          {document.status === 'pending' && (
                            <DropdownMenuItem onClick={() => onReviewDocument(document)}>
                              <FilePenLine className="mr-2 h-4 w-4" />
                              Review
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </ResponsiveTable>
      )}
    </div>
  );
}
